import { type FC, useMemo } from 'react'
import classNames from 'classnames'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { dropdownMessage, dropdownSectionHeader } from '../../../components/SelectDropdown/dropdownOptions'
import { useContactSearch } from '../../../common/useContactSearch'
import { contactOptionOf, filterByClientStatus, holderClientStatus } from '../domain/contactFallback'
import { WARNING_BORDER } from '../constants'
import { type ContactOption, type WizardHolder } from '../domain/types'

const _MANUAL_HOLDER = '__manual__'

const _MANUAL_OPTION: SelectOption = { id: _MANUAL_HOLDER, label: 'Someone else…' }

const _NO_MATCH_MESSAGE = 'No matching contacts. Choose “Someone else…” to type a name.'

const _NO_LINKED_MATCH_MESSAGE = 'No matching contacts are linked to the custodian.'

// Results are filtered by custodian link client-side, so a larger page keeps the list from running thin.
const _SEARCH_PAGE_SIZE = 200

interface HolderPersonPickerProps {
  accountId: string
  connector: string
  holder: WizardHolder
  toggleId: string
  disabled: boolean
  suggested: ContactOption[]
  onPick: (contact: ContactOption | null) => void
  onTypeByHand: () => void
}

export const HolderPersonPicker: FC<HolderPersonPickerProps> = ({
  accountId,
  connector,
  holder,
  toggleId,
  disabled,
  suggested: allSuggested,
  onPick,
  onTypeByHand,
}) => {
  const { data: searchResults, search, setSearch, isFetching } = useContactSearch({ accountId, pageSize: _SEARCH_PAGE_SIZE })
  const status = holderClientStatus(holder)
  const existingClient = status === 'existing'

  const suggested = useMemo(() => filterByClientStatus(allSuggested, status, connector), [allSuggested, status, connector])

  const found = useMemo(
    () =>
      filterByClientStatus(searchResults.map(contactOptionOf), status, connector).filter(
        (option) => !suggested.some((candidate) => candidate.id === option.id),
      ),
    [searchResults, suggested, status, connector],
  )

  const options = useMemo(() => {
    const sections: SelectOption[] = []
    const people = [...suggested, ...found]
    if (people.length > 0) {
      sections.push(dropdownSectionHeader('contacts', 'Contacts'), ...people.map((option) => ({ id: option.id, label: option.name })))
    }
    if (sections.length === 0 && (existingClient || (search ?? '').trim().length > 0) && !isFetching) {
      sections.push(dropdownMessage(existingClient ? _NO_LINKED_MATCH_MESSAGE : _NO_MATCH_MESSAGE))
    }
    const linkedElsewhere =
      holder.nameSource === 'contact' &&
      holder.contactId &&
      !suggested.some((option) => option.id === holder.contactId) &&
      !found.some((option) => option.id === holder.contactId)
    if (linkedElsewhere && holder.contactId) {
      sections.push({ id: holder.contactId, label: holder.name })
    }
    return existingClient ? sections : [...sections, _MANUAL_OPTION]
  }, [suggested, found, search, isFetching, existingClient, holder.nameSource, holder.contactId, holder.name])

  const selected = useMemo(() => {
    if (holder.nameSource === 'manual') {
      return _MANUAL_OPTION
    }
    return holder.nameSource === 'contact' && holder.contactId ? { id: holder.contactId, label: holder.name } : null
  }, [holder.nameSource, holder.contactId, holder.name])

  const handleSelected = (option: SelectOption) => {
    if (option.id === _MANUAL_HOLDER) {
      onTypeByHand()
      return
    }
    onPick([...suggested, ...found].find((candidate) => candidate.id === option.id) ?? null)
  }

  return (
    <SelectDropdown
      toggleId={toggleId}
      options={options}
      initialSelectedOption={selected}
      placeholder='Search contacts…'
      searchable
      loading={isFetching}
      inputAutoComplete='off'
      disabled={disabled}
      className={classNames(holder.nameSource === null ? [WARNING_BORDER, 'rounded-2'] : null)}
      onSearchInputChange={async (term) => setSearch(term)}
      onOptionSelected={handleSelected}
    />
  )
}
